import supabase from './supabase';

class DynamicOptionsService {
  // ============ LEAD SOURCES ============
  
  // Get all lead sources for an organization
  async getLeadSources(organizationId) {
    if (!organizationId) {
      console.warn('getLeadSources called without organizationId. Returning empty array.');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching lead sources:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getLeadSources:', error);
      throw error;
    }
  }

  // Create new lead source
  async createLeadSource(name, organizationId) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('lead_sources')
        .insert([{
          organization_id: organizationId,
          name: name.trim(),
          created_by: user.id,
          is_active: true
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating lead source:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createLeadSource:', error);
      throw error;
    }
  }

  // Update lead source
  async updateLeadSource(id, name) {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .update({
          name: name.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating lead source:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateLeadSource:', error);
      throw error;
    }
  }

  // Delete lead source (soft delete - mark as inactive)
  async deleteLeadSource(id) {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error deleting lead source:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in deleteLeadSource:', error);
      throw error;
    }
  }

  // ============ PRIMARY SERVICES ============
  
  // Get all primary services for an organization
  async getPrimaryServices(organizationId) {
    if (!organizationId) {
      console.warn('getPrimaryServices called without organizationId. Returning empty array.');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('primary_services')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching primary services:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getPrimaryServices:', error);
      throw error;
    }
  }

  // Create new primary service
  async createPrimaryService(name, organizationId) {
    if (!organizationId) {
      throw new Error('Organization ID is required');
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('primary_services')
        .insert([{
          organization_id: organizationId,
          name: name.trim(),
          created_by: user.id,
          is_active: true
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating primary service:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createPrimaryService:', error);
      throw error;
    }
  }

  // Update primary service
  async updatePrimaryService(id, name) {
    try {
      const { data, error } = await supabase
        .from('primary_services')
        .update({
          name: name.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating primary service:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updatePrimaryService:', error);
      throw error;
    }
  }

  // Delete primary service (soft delete - mark as inactive)
  async deletePrimaryService(id) {
    try {
      const { data, error } = await supabase
        .from('primary_services')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error deleting primary service:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in deletePrimaryService:', error);
      throw error;
    }
  }

  // ============ UTILITY METHODS ============

  // Get lead source by ID
  async getLeadSourceById(id) {
    try {
      const { data, error } = await supabase
        .from('lead_sources')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching lead source by ID:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getLeadSourceById:', error);
      throw error;
    }
  }

  // Get primary service by ID
  async getPrimaryServiceById(id) {
    try {
      const { data, error } = await supabase
        .from('primary_services')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching primary service by ID:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getPrimaryServiceById:', error);
      throw error;
    }
  }

  // Check if lead source name already exists in organization
  async checkLeadSourceExists(name, organizationId = null, excludeId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      let query = supabase
        .from('lead_sources')
        .select('id')
        .eq('organization_id', orgId)
        .eq('name', name.trim())
        .eq('is_active', true);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error checking lead source exists:', error);
        throw error;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error in checkLeadSourceExists:', error);
      throw error;
    }
  }

  // Check if primary service name already exists in organization
  async checkPrimaryServiceExists(name, organizationId = null, excludeId = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get organization ID if not provided
      let orgId = organizationId;
      if (!orgId) {
        const { data: orgData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        orgId = orgData?.organization_id;
      }

      if (!orgId) {
        throw new Error('Organization ID not found');
      }

      let query = supabase
        .from('primary_services')
        .select('id')
        .eq('organization_id', orgId)
        .eq('name', name.trim())
        .eq('is_active', true);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error checking primary service exists:', error);
        throw error;
      }

      return data && data.length > 0;
    } catch (error) {
      console.error('Error in checkPrimaryServiceExists:', error);
      throw error;
    }
  }

  // Clear lead source from all contacts using it
  async clearLeadSourceFromContacts(leadSourceId) {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ lead_source_id: null })
        .eq('lead_source_id', leadSourceId);

      if (error) {
        console.error('Error clearing lead source from contacts:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in clearLeadSourceFromContacts:', error);
      throw error;
    }
  }

  // Clear primary service from all contacts using it
  async clearPrimaryServiceFromContacts(primaryServiceId) {
    try {
      const { error } = await supabase
        .from('contacts')
        .update({ primary_service_id: null })
        .eq('primary_service_id', primaryServiceId);

      if (error) {
        console.error('Error clearing primary service from contacts:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in clearPrimaryServiceFromContacts:', error);
      throw error;
    }
  }
}

export default new DynamicOptionsService(); 